import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useQueries } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { loadPeakTone } from "@/lib/badgeTones";
import { useUnits, UNIT_LOAD_KEY } from "@/hooks/useUnits";
import { ApiError } from "@/api/client";
import { getUnitLoad, type UnitLoadDay } from "@/api/units";
import { toDateKey } from "@/lib/dateKey";

interface UnitSummary {
  id: number;
  name: string;
  peakHours: number;
  capacity: number;
  overloadDays: number;
  peakRatio: number;
}

function dotClass(ratio: number): string {
  // Цвет точки повторяет solid-вариант тона из `loadPeakTone`.
  // Менять оттенки — в `tokens.css` (`--tone-*-solid-bg`).
  return `tone-${loadPeakTone(ratio).tone}-solid`;
}

export function LoadSummaryWidget(): React.ReactElement {
  const { t } = useTranslation();
  const unitsQuery = useUnits();

  const range = useMemo(() => {
    const today = new Date();
    const from = toDateKey(today);
    const to = new Date(today);
    to.setDate(to.getDate() + 14);
    return { from, to: toDateKey(to) };
  }, []);

  const units = useMemo(() => unitsQuery.data ?? [], [unitsQuery.data]);

  const loadQueries = useQueries({
    queries: units.map((u) => ({
      queryKey: UNIT_LOAD_KEY(u.id ?? 0, range),
      queryFn: () => getUnitLoad(u.id ?? 0, range),
      enabled: (u.id ?? 0) > 0,
      retry: (failureCount: number, error: Error) => {
        if (error instanceof ApiError && error.status === 401) return false;
        return failureCount < 1;
      },
      staleTime: 60_000,
    })),
  });

  const summaries = useMemo<UnitSummary[]>(() => {
    const out: UnitSummary[] = [];
    units.forEach((u, idx) => {
      const days = (loadQueries[idx]?.data ?? []) as UnitLoadDay[];
      if (days.length === 0) return;
      let peakHours = 0;
      let peakCapacity = 0;
      let peakRatio = 0;
      let overloadDays = 0;
      for (const d of days) {
        // Both sides are man-hours straight from the API (task 23 §4.3):
        // capacity is the unit's member-derived availability, load is
        // people × effective hours.
        const hours = d.load_hours;
        const capacity = d.capacity_hours;
        const ratio = capacity === 0 ? 0 : hours / capacity;
        if (ratio > peakRatio) {
          peakRatio = ratio;
          peakHours = hours;
          peakCapacity = capacity;
        }
        if (d.load_hours > d.capacity_hours) overloadDays += 1;
      }
      out.push({
        id: u.id ?? 0,
        name: u.name,
        peakHours: Math.round(peakHours),
        capacity: Math.round(peakCapacity),
        overloadDays,
        peakRatio,
      });
    });
    out.sort((a, b) => b.peakRatio - a.peakRatio);
    return out.slice(0, 5);
  }, [units, loadQueries]);

  const isLoading = unitsQuery.isLoading || loadQueries.some((q) => q.isLoading);
  const isError = unitsQuery.isError || loadQueries.some((q) => q.isError);
  const error = unitsQuery.error ?? loadQueries.find((query) => query.error)?.error ?? null;
  const allCalm = summaries.every((s) => s.peakRatio < 0.85);

  return (
    <Card data-testid="widget-load-summary">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{t("home.widgets.loadSummary.title")}</CardTitle>
        <span className="text-muted-foreground text-xs">
          {t("home.widgets.loadSummary.subtitle")}
        </span>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <ul className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="bg-muted/40 h-6 animate-pulse rounded" />
            ))}
          </ul>
        ) : isError ? (
          <ApiErrorNotice
            error={error}
            fallbackMessage={t("common.apiError.fallback")}
            className="text-destructive text-sm"
          />
        ) : summaries.length === 0 || allCalm ? (
          <p className="text-muted-foreground text-sm">{t("home.widgets.loadSummary.empty")}</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {summaries.map((s) => (
              <li key={s.id}>
                <Link
                  to="/load"
                  search={{ unit: s.id }}
                  data-testid={`widget-load-row-${s.id}`}
                  className="hover:bg-accent flex items-center justify-between gap-2 rounded px-1.5 py-1"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass(s.peakRatio)}`} />
                    <span className="truncate font-medium">{s.name}</span>
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                    {t("home.widgets.loadSummary.rowFmt", {
                      peak: s.peakHours,
                      capacity: s.capacity,
                      overload: s.overloadDays,
                    })}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
